import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Tag, Clock, Copy, ChevronRight, Gift } from 'lucide-react'
import TopHeader from '../components/layout/TopHeader.jsx'
import { promotionService } from '../services/api.js'
import toast from 'react-hot-toast'

// ── Helpers ────────────────────────────────────────────────────────────────────
function timeLeft(endDate) {
  if (!endDate) return null
  const diff = new Date(endDate).getTime() - Date.now()
  if (diff <= 0) return 'Expired'
  const d = Math.floor(diff / 86400000)
  const h = Math.floor((diff % 86400000) / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  if (d > 0) return `${d}d ${h}h left`
  if (h > 0) return `${h}h ${m}m left`
  return `${m}m left`
}

function discountLabel(p) {
  if (p.discount_type === 'percentage') return `${p.discount_value}% OFF`
  if (p.discount_type === 'fixed')      return `R${p.discount_value} OFF`
  if (p.discount_type === 'free_shipping') return 'FREE DELIVERY'
  return 'SPECIAL OFFER'
}

function promoColor(p) {
  if (p.discount_type === 'percentage' && p.discount_value >= 30) return { bg: '#7C3AED', light: '#7C3AED22' }
  if (p.discount_type === 'percentage' && p.discount_value >= 15) return { bg: '#DC2626', light: '#DC262622' }
  if (p.discount_type === 'fixed')         return { bg: '#D97706', light: '#D9770622' }
  if (p.discount_type === 'free_shipping') return { bg: '#059669', light: '#05966922' }
  return { bg: '#0e947a', light: '#0e947a22' }
}

function CountdownBadge({ endDate }) {
  const label = timeLeft(endDate)
  if (!label) return null
  const urgent = label !== 'Expired' && label.includes('m left') && !label.includes('h')
  return (
    <div className={`flex items-center gap-1 text-[11px] font-display font-semibold ${urgent ? 'text-red-400' : 'text-gray-400'}`}>
      <Clock size={11} />
      {label}
    </div>
  )
}

function PromoCard({ promo, onCopy }) {
  const colors = promoColor(promo)
  const badge  = discountLabel(promo)

  return (
    <div className="bg-surface-card rounded-2xl border border-surface-border overflow-hidden">
      {/* Colour band */}
      <div className="h-1.5 w-full" style={{ background: colors.bg }} />
      <div className="p-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-display font-bold px-2 py-0.5 rounded-lg"
                style={{ background: colors.light, color: colors.bg }}>
                {badge}
              </span>
            </div>
            <h3 className="font-heading text-white text-base leading-tight">{promo.title}</h3>
          </div>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: colors.light }}>
            <Gift size={20} style={{ color: colors.bg }} />
          </div>
        </div>

        {promo.description && (
          <p className="font-display text-gray-400 text-sm leading-relaxed mb-3">{promo.description}</p>
        )}


        {/* Footer */}
        <div className="flex items-center justify-between">
          <CountdownBadge endDate={promo.ends_at} />
          {promo.min_order_value > 0 && (
            <span className="text-gray-500 text-xs font-display">
              Min. order R{parseFloat(promo.min_order_value || 0).toFixed(2)}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function Promotions() {
  const navigate     = useNavigate()
  const queryClient  = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['promotions', 'customer'],
    queryFn: promotionService.getAll,
    staleTime: 2 * 60 * 1000,
  })

  const promos = (Array.isArray(data) ? data : (data?.promotions ?? data?.items ?? []))
    .filter(p => !p.dismissed)

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(() => {
      toast.success(`Code "${code}" copied!`, { icon: '🏷️' })
    }).catch(() => {
      toast(`Code: ${code}`, { icon: '🏷️', duration: 4000 })
    })
  }

  return (
    <div className="page-container bg-surface-dark">
      <TopHeader title="Promotions" showBack backTo="/" />

      {/* Hero strip */}
      <div className="mx-4 mt-2 mb-4 bg-gradient-to-r from-primary-dark to-primary rounded-2xl p-4 flex items-center gap-4">
        <span className="text-4xl">🎁</span>
        <div>
          <p className="font-heading text-white text-base">Active Deals</p>
          <p className="font-display text-gray-200 text-sm">Exclusive offers for you</p>
        </div>
        <button onClick={() => navigate('/shop')}
          className="ml-auto btn-gold py-2 px-3 text-xs whitespace-nowrap flex items-center gap-1">
          Shop <ChevronRight size={12} />
        </button>
      </div>

      {/* Promo list */}
      <div className="px-4 pb-8 space-y-3">
        {isLoading ? (
          Array(3).fill(0).map((_, i) => (
            <div key={i} className="bg-surface-card rounded-2xl border border-surface-border h-36 animate-pulse" />
          ))
        ) : error ? (
          <div className="text-center py-16">
            <span className="text-4xl mb-3 block">⚠️</span>
            <p className="text-gray-400 font-display text-sm mb-4">Failed to load promotions</p>
            <button onClick={refetch} className="btn-outline py-2 px-5 text-sm">Retry</button>
          </div>
        ) : promos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="text-5xl mb-4">🏷️</span>
            <p className="text-white font-heading text-lg mb-1">No active promotions</p>
            <p className="text-gray-400 font-display text-sm">Check back soon for great deals!</p>
            <button onClick={() => navigate('/shop')} className="btn-primary mt-5 py-2.5 px-6">
              Browse Products
            </button>
          </div>
        ) : (
          promos.map(p => <PromoCard key={p.id} promo={p} onCopy={copyCode} />)
        )}
      </div>
    </div>
  )
}
